import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import "./ScreeningRoom.scss";
import {
  holdPageAtTop,
  isPinningPageTop,
  resetPageScroll,
} from "./resetPageScroll";
import CinemaDraw, { CINEMA_DRAW_ENABLED, useCinemaDraw } from "./CinemaDraw";

// ─────────────────────────────────────────────────────────────────────────────
// SCREENING ROOM — a private cinema: recessed screen + framed poster archive.
//
// HOW TO EDIT:
//   • Each entry in CINEMA needs { title, year, type, poster, still }.
//   • by    — director or creator, shown on the museum placard.
//   • glow  — "r, g, b" for the faint cinema-light on the surrounding walnut.
//   • poster — vertical theatrical/series one-sheet (~2:3).
//   • still  — landscape cinematic still shown on the built-in screen.
//   • type   — "film" or "series" (reserved for later filters).
//   • video  — optional YouTube URL (or bare ID). The built-in screen plays it,
//              muted and looping, once browsing settles on that title. The
//              still shows underneath until the picture arrives, and remains
//              the only thing shown for titles with no video.
//   • videoStart — optional seconds offset to skip a cold open or logo card.
// ─────────────────────────────────────────────────────────────────────────────

const CINEMA = [
  {
    title: "Top Gun: Maverick",
    year: "2022",
    type: "film",
    by: "Joseph Kosinski",
    glow: "90, 155, 215",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMDBkZDNjMWEtOTdmMi00NmExLTg5MmMtNTFlYTJlNWY5YTdmXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/AaV1YIdWKnjAIAOe8UUKBFm327v.jpg",
    video: "https://www.youtube.com/watch?v=JaXDM-5_3do",
  },
  {
    title: "The Matrix",
    year: "1999",
    type: "film",
    by: "The Wachowskis",
    glow: "40, 170, 175",
    poster:
      "https://m.media-amazon.com/images/M/MV5BN2NmN2VhMTQtMDNiOS00NDlhLTliMjgtODE2ZTY0ODQyNDRhXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still: "/stills/the-matrix-4k.jpg",
    video: "https://www.youtube.com/watch?v=Vy7RaQUmOzE",
  },
  {
    title: "Batman Begins",
    year: "2005",
    type: "film",
    by: "Christopher Nolan",
    glow: "70, 95, 160",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMzA2NDQzZDEtNDU5Ni00YTlkLTg2OWEtYmQwM2Y1YTBjMjFjXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/9IIBboV7MCT0bTxzXHmWK1Hq558.jpg",
    video: "https://www.youtube.com/watch?v=FiL1_5DWV7Y",
  },
  {
    title: "The Dark Knight",
    year: "2008",
    type: "film",
    by: "Christopher Nolan",
    glow: "50, 70, 120",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMTMxNTMwODM0NF5BMl5BanBnXkFtZTcwODAyMTk2Mw@@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/cfT29Im5VDvjE0RpyKOSdCKZal7.jpg",
    video: "https://www.youtube.com/watch?v=EqG3_v4e-Z4",
  },
  {
    title: "Star Trek",
    year: "2009",
    type: "film",
    by: "J.J. Abrams",
    glow: "140, 185, 230",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMjE5NDQ5OTE4Ml5BMl5BanBnXkFtZTcwOTE3NDIzMw@@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/q7M0JpPixbEYT8EhnI7wTEMONxz.jpg",
    video: "https://www.youtube.com/watch?v=HNwUJzlZVzc",
  },
  {
    title: "Arcane",
    year: "2021–2024",
    type: "series",
    by: "Christian Linke & Alex Yee",
    glow: "110, 80, 210",
    poster:
      "https://image.tmdb.org/t/p/w780/t0oQXskUt5xqNmUgiBAIkMzfCob.jpg",
    still:
      "https://image.tmdb.org/t/p/original/q8eejQcg1bAqImEV8jh8RtBD4uH.jpg",
    video: "https://www.youtube.com/watch?v=fXmAurh012s",
  },
  {
    title: "Dune: Part Two",
    year: "2024",
    type: "film",
    by: "Denis Villeneuve",
    glow: "200, 155, 70",
    poster:
      "https://m.media-amazon.com/images/M/MV5BNTc0YmQxMjEtODI5MC00NjFiLTlkMWUtOGQ5NjFmYWUyZGJhXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still: "/stills/dune-part-two-4k.jpg",
    video: "https://www.youtube.com/watch?v=BYMA4Ag3CGE",
  },
  {
    title: "Game of Thrones",
    year: "2011–2019",
    type: "series",
    by: "David Benioff & D.B. Weiss",
    glow: "150, 165, 190",
    poster:
      "https://image.tmdb.org/t/p/w780/fw9hNiJO0U5MUg2XGyCOlTS3jdS.jpg",
    still:
      "https://image.tmdb.org/t/p/original/2OMB0ynKlyIenMJWI2Dy9IWT4c.jpg",
    video: "https://www.youtube.com/watch?v=O-O8Er94TJI",
  },
  {
    title: "The Lord of the Rings trilogy",
    year: "2001–2003",
    type: "film",
    by: "Peter Jackson",
    glow: "185, 150, 65",
    poster:
      "https://m.media-amazon.com/images/M/MV5BNzIxMDQ2YTctNDY4MC00ZTRhLTk4ODQtMTVlOWY4NTdiYmMwXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still: "/stills/lord-of-the-rings-4k.jpg",
    video: "https://www.youtube.com/watch?v=c24-0Amwyik",
  },
  {
    title: "Breaking Bad",
    year: "2008–2013",
    type: "series",
    by: "Vince Gilligan",
    glow: "195, 140, 45",
    poster:
      "https://m.media-amazon.com/images/M/MV5BOWE4NTc3YmYtNmU2Mi00ZjhkLWE1MTItZmM1M2U1ODU3YjFlXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/tsRy63Mu5cu8etL1X7ZLyf7UP1M.jpg",
    video: "https://www.youtube.com/watch?v=zAjJYkUnTEs",
  },
  {
    title: "Silicon Valley",
    year: "2014–2019",
    type: "series",
    by: "Mike Judge",
    glow: "175, 185, 165",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMjhhYmZjZjgtOTc3Mi00ZmY3LWI3MTUtYzcyOGMxYWFmN2YyXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/4pfXAnWxOfEJsUgDPW0zqzs5UWv.jpg",
    video: "https://www.youtube.com/watch?v=Vm4tx1O9GAc",
  },
  {
    title: "Mission: Impossible – Fallout",
    year: "2018",
    type: "film",
    by: "Christopher McQuarrie",
    glow: "45, 75, 140",
    poster:
      "https://image.tmdb.org/t/p/w780/xyAgXPqAm2ogp54tYeJpvRbEnKL.jpg",
    still: "https://images8.alphacoders.com/112/thumb-1920-1123406.jpg",
    video: "https://www.youtube.com/watch?v=HsMVFxZ43iU",
  },
  {
    title: "Jurassic Park",
    year: "1993",
    type: "film",
    by: "Steven Spielberg",
    glow: "95, 165, 70",
    poster:
      "https://image.tmdb.org/t/p/w780/63viWuPfYQjRYLSZSZNq7dglJP5.jpg",
    still: "/stills/jurassic-park-4k.jpg",
    video: "https://www.youtube.com/watch?v=Rc_i5TKdmhs",
  },
  {
    title: "Heat",
    year: "1995",
    type: "film",
    by: "Michael Mann",
    glow: "215, 140, 45",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMTkxYjU1OTMtYWViZC00ZjAzLWI3MDktZGQ2N2VmMjVjNDRlXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/xKsnZDERG1dk95wuZ5q9iks3OL3.jpg",
    video: "https://www.youtube.com/watch?v=h7N1gsQY4Io",
  },
  {
    title: "Casino Royale",
    year: "2006",
    type: "film",
    by: "Martin Campbell",
    glow: "165, 145, 70",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMWQ1ZDM4NDktMWY0NC00MjcxLWJlMDMtNmE2MGVhYzRjMWQ0XkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/klJMCIblHLFwCuGjKz7tyOpekIC.jpg",
    // Your link (4iOdYf9w8no) has embedding disabled, so this is the official
    // James Bond 007 channel's upload.
    video: "https://www.youtube.com/watch?v=GV_18deeAXk",
  },
  {
    title: "Skyfall",
    year: "2012",
    type: "film",
    by: "Sam Mendes",
    glow: "125, 145, 175",
    poster:
      "https://image.tmdb.org/t/p/w780/izrHg2UzxG3YXTBcKFaUbYp9LWA.jpg",
    still: "/stills/skyfall-4k.jpg",
    video: "https://www.youtube.com/watch?v=qSdsimsodZs",
  },
  {
    title: "Project Hail Mary",
    year: "2026",
    type: "film",
    by: "Phil Lord & Christopher Miller",
    glow: "70, 175, 215",
    poster:
      "https://m.media-amazon.com/images/M/MV5BNTkwNzJiYTctNzI3NC00NjE1LTlhYjktY2Q5MTdmMWFmNzcxXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/8Tfys3mDZVp4tNoH2ktm06a0Tau.jpg",
    video: "https://www.youtube.com/watch?v=m08TxIsFTRI",
  },
  {
    title: "The Martian",
    year: "2015",
    type: "film",
    by: "Ridley Scott",
    glow: "215, 150, 70",
    poster:
      "https://image.tmdb.org/t/p/w780/fASz8A0yFE3QB6LgGoOfwvFSseV.jpg",
    still:
      "https://image.tmdb.org/t/p/original/lzMS0CI3FLQYC5EgJoWeIaEt0lm.jpg",
    video: "https://www.youtube.com/watch?v=ej3ioOneTy8",
  },
  {
    title: "24",
    year: "2001–2010",
    type: "series",
    by: "Joel Surnow & Robert Cochran",
    glow: "185, 55, 40",
    poster:
      "https://image.tmdb.org/t/p/w780/iq6yrZ5LEDXf1ArCOYLq8PIUBpV.jpg",
    still:
      "https://image.tmdb.org/t/p/original/be6mDIMv7cg8duWkcYVnTB8rphO.jpg",
    video: "https://www.youtube.com/watch?v=-Tldme1j_Ec",
  },
  {
    title: "Harry Potter series",
    year: "2001–2011",
    type: "film",
    by: "Chris Columbus, Alfonso Cuarón, Mike Newell & David Yates",
    glow: "190, 150, 55",
    poster:
      "https://image.tmdb.org/t/p/w780/eVPs2Y0LyvTLZn6AP5Z6O2rtiGB.jpg",
    still:
      "https://image.tmdb.org/t/p/original/1XAC6RPT01UX9EQGy2JVn5c8pgy.jpg",
    video: "https://www.youtube.com/watch?v=T-Yi49pHlEo",
  },
  {
    title: "Stranger Things",
    year: "2016–2025",
    type: "series",
    by: "The Duffer Brothers",
    glow: "200, 40, 50",
    poster: "/posters/stranger-things.png",
    still:
      "https://image.tmdb.org/t/p/original/56v2KjBlU4XaOv9rVYEQypROD7P.jpg",
    video: "https://www.youtube.com/watch?v=mnd7sFt5c3A",
  },
  {
    title: "Minority Report",
    year: "2002",
    type: "film",
    by: "Steven Spielberg",
    glow: "90, 205, 215",
    poster:
      "https://m.media-amazon.com/images/M/MV5BYTdmNzM3YzItZTRkYS00MTY5LTg4ZmItNjFmNTk2N2Q2NmNlXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/PrMb3oeEluauy0q9ZO5xL33A6C.jpg",
    video: "https://www.youtube.com/watch?v=3JNsNp2DVSE",
  },
  {
    title: "Fleabag",
    year: "2016–2019",
    type: "series",
    by: "Phoebe Waller-Bridge",
    glow: "185, 95, 80",
    poster: "/posters/fleabag.png",
    still: "/stills/fleabag.jpg",
    video: "https://www.youtube.com/watch?v=aX2ViKQFL_k",
  },
  {
    title: "Chernobyl",
    year: "2019",
    type: "series",
    by: "Craig Mazin",
    glow: "165, 175, 55",
    poster:
      "https://m.media-amazon.com/images/M/MV5BNzU0OTI4YTQtNGQ1ZS00ZjA4LTg3MTMtZjkyZWNjN2RiZDJmXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/3URK0z9PzpVNJrGE7XOuyy6KFzk.jpg",
  },
  {
    title: "How to Train Your Dragon",
    year: "2010",
    type: "film",
    by: "Chris Sanders & Dean DeBlois",
    glow: "80, 125, 185",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMjA5NDQyMjc2NF5BMl5BanBnXkFtZTcwMjg5ODcyMw@@._V1_QL75_UX780_.jpg",
    still: "/stills/how-to-train-your-dragon.jpg",
    video: "https://www.youtube.com/watch?v=IvFBobchMoc",
  },
  {
    title: "Monsters, Inc.",
    year: "2001",
    type: "film",
    by: "Pete Docter",
    glow: "40, 185, 195",
    poster:
      "https://image.tmdb.org/t/p/w780/wFSpyMsp7H0ttERbxY7Trlv8xry.jpg",
    still:
      "https://image.tmdb.org/t/p/original/sDTnMOJ3H5wI38OxObmCtK7wfd5.jpg",
    video: "https://www.youtube.com/watch?v=7IFVPL28Y9A",
  },
  {
    title: "Inside Out 2",
    year: "2024",
    type: "film",
    by: "Kelsey Mann",
    glow: "245, 155, 70",
    poster:
      "https://image.tmdb.org/t/p/w780/vpnVM9B6NMmQpWeZvzLvDESb2QY.jpg",
    still:
      "https://image.tmdb.org/t/p/original/p5ozvmdgsmbWe0H8Xk7Rc8SCwAB.jpg",
    video: "https://www.youtube.com/watch?v=LEjhY15eCx0",
  },
  {
    title: "Rick and Morty",
    year: "2013–2022 · seasons 1–6",
    type: "series",
    by: "Justin Roiland & Dan Harmon",
    glow: "85, 220, 90",
    poster:
      "https://m.media-amazon.com/images/M/MV5BZGQyZjk2MzMtMTcyNC00NGU3LTlmNjItNDExMWM4ZDFhYmQ2XkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/iFOkrSrJRwE27PwbyQeYLlMJXzw.jpg",
  },
  {
    title: "The Godfather",
    year: "1972",
    type: "film",
    by: "Francis Ford Coppola",
    glow: "185, 140, 65",
    poster:
      "https://image.tmdb.org/t/p/w780/3bhkrj58Vtu7enYsRolD1fZdja1.jpg",
    still:
      "https://image.tmdb.org/t/p/original/tmU7GeKVybMWFButWEGl2M4GeiP.jpg",
    video: "https://www.youtube.com/watch?v=UaVTIH8mujA",
  },
  {
    title: "The Girl with the Dragon Tattoo",
    year: "2011",
    type: "film",
    by: "David Fincher",
    glow: "165, 185, 205",
    poster:
      "https://image.tmdb.org/t/p/w780/8bokS83zGdhaXgN9tjidUKmAftW.jpg",
    still:
      "https://image.tmdb.org/t/p/original/dNFbrnF0mIBm0rClbfEFWTtsgMP.jpg",
    video: "https://www.youtube.com/watch?v=DqQe3OrsMKI",
  },
  {
    title: "The Hunt for Red October",
    year: "1990",
    type: "film",
    by: "John McTiernan",
    glow: "40, 70, 130",
    poster:
      "https://image.tmdb.org/t/p/w780/yVl7zidse4KiWtGMqHFtZCx4X3N.jpg",
    still:
      "https://image.tmdb.org/t/p/original/5jCpCFrku6wmCZOYZWhHOC2j8IU.jpg",
    video: "https://www.youtube.com/watch?v=NSiAsQMBSRA",
  },
  {
    title: "Tropic Thunder",
    year: "2008",
    type: "film",
    by: "Ben Stiller",
    glow: "205, 130, 50",
    poster:
      "https://image.tmdb.org/t/p/w780/zAurB9mNxfYRoVrVjAJJwGV3sPg.jpg",
    still:
      "https://image.tmdb.org/t/p/original/4pLi4QMm0MZ9TSoF3l8qQmY9HNO.jpg",
  },
  {
    title: "Veep",
    year: "2012–2019",
    type: "series",
    by: "Armando Iannucci",
    glow: "190, 185, 165",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMjE2NDM0OTEwMl5BMl5BanBnXkFtZTgwNzgwNDI0ODE@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/vKaDMLbdLrThldFj9Vavo6suTS2.jpg",
    video: "https://www.youtube.com/watch?v=I2HoCG_iyhU",
  },
  {
    title: "Seinfeld",
    year: "1989–1998",
    type: "series",
    by: "Jerry Seinfeld & Larry David",
    glow: "215, 180, 140",
    poster:
      "https://m.media-amazon.com/images/M/MV5BMmRjNjZjN2ItN2FkYi00ZDg0LWExN2EtMTU2ODUwNWU1M2NhXkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/tMRdEsMg9MyXAkcufK9YJEeIaNW.jpg",
  },
  {
    title: "Frasier",
    year: "1993–2004",
    type: "series",
    by: "David Lee, Peter Casey & David Angell",
    glow: "170, 160, 130",
    poster:
      "https://m.media-amazon.com/images/M/MV5BYTYwMjY1OTctZjk4My00ZjllLWFkNTMtNzg2OGVlMGI0MmQ1XkEyXkFqcGc@._V1_QL75_UX780_.jpg",
    still:
      "https://image.tmdb.org/t/p/original/4FqKFhF4BrNsrK3EdRpVJofVqCp.jpg",
  },
  {
    title: "Friends",
    year: "1994–2004",
    type: "series",
    by: "David Crane & Marta Kauffman",
    glow: "155, 115, 185",
    poster:
      "https://image.tmdb.org/t/p/w780/2koX1xLkpTQM4IZebYvKysFW1Nh.jpg",
    still:
      "https://image.tmdb.org/t/p/original/l0qVZIpXtIo7km9u5Yqh0nKPOr5.jpg",
  },
  {
    title: "Vengeance",
    year: "2022",
    type: "film",
    by: "B.J. Novak",
    glow: "215, 170, 80",
    poster:
      "https://image.tmdb.org/t/p/w780/n1SlTeEv3dNJB3gF9AHAVElQ6LO.jpg",
    still:
      "https://image.tmdb.org/t/p/original/jwFBzWabfWpnN9P5YIVVX5W8WUY.jpg",
    video: "https://www.youtube.com/watch?v=1GT1jFsNnPw",
    videoStart: 19,
  },
  {
    title: "Something's Gotta Give",
    year: "2003",
    type: "film",
    by: "Nancy Meyers",
    glow: "215, 195, 145",
    poster:
      "https://image.tmdb.org/t/p/w780/1cpdqe0SpiHzzbOLq9GDUUlZdSl.jpg",
    still:
      "https://image.tmdb.org/t/p/original/xTQXtSQQZY5ZbJ1GxEcSCRP4aWV.jpg",
    video: "https://www.youtube.com/watch?v=b3-dKZA4fGk",
    videoStart: 2,
  },
  {
    title: "When Harry Met Sally...",
    year: "1989",
    type: "film",
    by: "Rob Reiner",
    glow: "205, 145, 90",
    poster:
      "https://image.tmdb.org/t/p/w780/rFOiFUhTMtDetqCGClC9PIgnC1P.jpg",
    still:
      "https://image.tmdb.org/t/p/original/orq7v7J0Esq9TWpKitCXPbevOdW.jpg",
    video: "https://www.youtube.com/watch?v=g4ODPyUxLNw",
  },

];

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Accepts a watch/share/embed URL or a bare 11-character ID.
function youTubeId(video) {
  if (!video) return "";
  const match = video.match(
    /(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{11})/
  );
  if (match) return match[1];
  return /^[A-Za-z0-9_-]{11}$/.test(video) ? video : "";
}

// The player is driven through the official IFrame API rather than raw
// postMessage, which is what makes unmuting actually take effect.
let ytApiPromise = null;

function loadYouTubeApi() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === "function") previous();
        resolve(window.YT);
      };
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    });
  }
  return ytApiPromise;
}

// Note there is deliberately no loop/playlist pair here. `loop` requires
// `playlist`, and that makes YouTube render playlist chrome — a pause button
// flanked by track skip arrows — in the dead centre of the frame, where
// cropping the edges cannot reach it. Looping is done from onStateChange
// instead. The logo itself cannot be removed by any parameter, so .screen-reel
// overscans and clips the player's edges to keep it out of frame.
function reelPlayerVars(startSeconds) {
  return {
    autoplay: 1,
    mute: 1,
    controls: 0,
    rel: 0,
    iv_load_policy: 3,
    cc_load_policy: 0,
    playsinline: 1,
    disablekb: 1,
    fs: 0,
    start: startSeconds || 0,
    // IFrame API: origin + widget_referrer identify this page to YouTube.
    // Without a Referer, embeds fail with Error 153 even when origin is set.
    origin: window.location.origin,
    widget_referrer: window.location.href,
    enablejsapi: 1,
  };
}

// cc_load_policy only declines to force captions on; it does not stop a video
// whose own default is captions-on, so the module is torn out as well. Both
// names are tried because the player has used each, and it is repeated on
// playback because the track can load in late.
function silenceCaptions(player) {
  if (!player) return;
  ["captions", "cc"].forEach((module) => {
    try {
      // Clear the active track first: a video whose default is captions-on can
      // paint one cue before the module itself is gone.
      player.setOption(module, "track", {});
    } catch (err) {
      /* option unavailable once the module is unloaded */
    }
    try {
      player.unloadModule(module);
    } catch (err) {
      /* module absent on this player build */
    }
  });
}

function hushReelFrame(player, title) {
  const frame = player && player.getIframe && player.getIframe();
  if (!frame) return;
  frame.setAttribute("tabindex", "-1");
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
  frame.setAttribute("allowfullscreen", "");
  frame.setAttribute("webkitallowfullscreen", "true");
  const allow = frame.getAttribute("allow") || "";
  if (!/(^|;)\s*fullscreen\b/i.test(allow)) {
    frame.setAttribute("allow", allow ? `${allow}; fullscreen` : "fullscreen");
  }
  if (title) frame.setAttribute("title", `${title} footage`);
  // Do not blur() here. playVideo() focuses the iframe; blurring it in the
  // same turn (or from a focusin pin) can cancel playback. Scroll is pinned
  // separately — playback wins.
}

function reelIframe(player) {
  try {
    return player && player.getIframe ? player.getIframe() : null;
  } catch (err) {
    return null;
  }
}

// playVideo() focuses the iframe; the browser then scroll-into-views that
// oversized (126%) frame and yanks the page off the title. On first open,
// always force the document top — do not restore a leftover Home Y.
function playReel(player) {
  if (!player || !player.playVideo) return;
  const x = window.scrollX;
  const y = window.scrollY;
  hushReelFrame(player);
  player.playVideo();
  const restore = () => {
    if (isPinningPageTop()) resetPageScroll();
    else window.scrollTo(x, y);
  };
  restore();
  requestAnimationFrame(() => {
    restore();
    requestAnimationFrame(restore);
  });
  [50, 150, 400, 900].forEach((t) => setTimeout(restore, t));
}

// How long the selection must hold before a player is mounted, so scrolling
// the shelf does not spawn an iframe for every title it passes.
const REEL_SETTLE_MS = 500;
// YouTube's centre chrome hides after a couple of seconds of playback. A 5.5s
// still-hold plus a matching currentTime gate plus BUFFERING resets kept a
// working player invisible for ~11s on production — it looked dead. Reveal
// once playback has actually been running this long. BUFFERING must not
// restart the clock (see onStateChange).
const REEL_REVEAL_MS = 2400;
// Sound arrives first and plays over the still, so a title announces itself
// before the screen changes. Silence holds until this point.
const REEL_AUDIO_LEAD_MS = 800;
const REEL_AUDIO_AT_MS = Math.max(0, REEL_REVEAL_MS - REEL_AUDIO_LEAD_MS);
// Playing this many seconds ahead of a title's chosen start means the controls
// have already vanished by the time the frame is revealed, so no footage the
// viewer is meant to see gets spent hidden.
const REEL_LEAD_IN = REEL_REVEAL_MS / 1000;

function reelTimings(item) {
  const start = item.videoStart || 0;
  const from = start > 0 ? Math.max(0, start - REEL_LEAD_IN) : 0;
  return { from, revealAt: start > 0 ? start : REEL_REVEAL_MS / 1000 };
}

const CINEMA_EXPAND_MS = 780;
const CINEMA_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const CINEMA_Z = "2147483000";
const WELL_Z = "6";

function viewportBox() {
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  if (vv) {
    return {
      left: vv.offsetLeft,
      top: vv.offsetTop,
      width: vv.width,
      height: vv.height,
    };
  }
  return {
    left: 0,
    top: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

function nativeFsElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.webkitCurrentFullScreenElement ||
    null
  );
}

function asFsPromise(value) {
  return value && typeof value.then === "function"
    ? value
    : Promise.resolve(value);
}

// Must be called from the Enter Cinema tap — not a timeout. The first
// requestFullscreen runs synchronously so the user gesture is still live.
// iOS Safari often refuses anything but <video>; we still try, then fall
// back. Android Chrome should succeed and hide tabs.
function requestOnElement(el) {
  if (!el) return Promise.reject(new Error("no element"));

  if (typeof el.requestFullscreen === "function") {
    try {
      return asFsPromise(
        el.requestFullscreen({ navigationUI: "hide" })
      ).catch(() => asFsPromise(el.requestFullscreen()));
    } catch (err) {
      try {
        return asFsPromise(el.requestFullscreen());
      } catch (err2) {
        return Promise.reject(err2);
      }
    }
  }

  const webkit = el.webkitRequestFullscreen || el.webkitRequestFullScreen;
  if (typeof webkit === "function") {
    try {
      return asFsPromise(webkit.call(el, { navigationUI: "hide" })).catch(() =>
        asFsPromise(webkit.call(el))
      );
    } catch (err) {
      try {
        return asFsPromise(webkit.call(el));
      } catch (err2) {
        return Promise.reject(err2);
      }
    }
  }

  return Promise.reject(new Error("no fullscreen method"));
}

let cinemaFsInflight = null;

function requestCinemaFullscreen(screenEl, player) {
  if (nativeFsElement()) return Promise.resolve(true);
  if (cinemaFsInflight) return cinemaFsInflight;

  const iframe = reelIframe(player);
  const coarse =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;

  const candidates = [];
  const push = (node) => {
    if (node && candidates.indexOf(node) === -1) candidates.push(node);
  };
  // Chrome consumes the user gesture on the first requestFullscreen, even
  // when it rejects. On a phone, the document is the reliable way to hide
  // tabs — try it first so a failed .screen request does not burn the tap.
  // Desktop keeps the cinema root first so only the picture goes fullscreen.
  if (coarse) {
    push(typeof document !== "undefined" ? document.documentElement : null);
    push(screenEl);
    push(iframe);
  } else {
    push(screenEl);
    push(typeof document !== "undefined" ? document.documentElement : null);
  }

  const next = (i) => {
    if (i >= candidates.length) {
      if (player && typeof player.requestFullscreen === "function") {
        try {
          return asFsPromise(player.requestFullscreen()).then(
            () => true,
            () => Promise.resolve(false)
          );
        } catch (err) {
          return Promise.resolve(false);
        }
      }
      return Promise.resolve(false);
    }
    return requestOnElement(candidates[i]).then(
      () => true,
      () => next(i + 1)
    );
  };
  cinemaFsInflight = next(0).finally(() => {
    cinemaFsInflight = null;
  });
  return cinemaFsInflight;
}

function exitNativeFs() {
  const exit =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.webkitCancelFullScreen;
  if (!exit || !nativeFsElement()) return;
  try {
    const result = exit.call(document);
    if (result && result.catch) result.catch(() => {});
  } catch (err) {
    /* already exited */
  }
}

// Once native FS is up, size against that element's box. Otherwise use
// the dynamic visual viewport so iOS chrome collapse is picked up.
function cinemaStageBox() {
  const fs = nativeFsElement();
  if (fs && fs !== document.documentElement && fs !== document.body) {
    const r = fs.getBoundingClientRect();
    if (r.width >= 2 && r.height >= 2) {
      return {
        left: r.left,
        top: r.top,
        width: r.width,
        height: r.height,
      };
    }
  }
  return viewportBox();
}

function fitCinemaStage(el, player) {
  if (!el) return;
  const box = cinemaStageBox();
  if (nativeFsElement() === el) {
    const reel = reelCoverSize(box);
    el.style.setProperty("--reel-w", `${reel.width}px`);
    el.style.setProperty("--reel-h", `${reel.height}px`);
    syncReelPlayer(player, box, true);
    return;
  }
  applyCinemaBox(el, box);
  syncReelPlayer(player, box, true);
}

// Screen Orientation lock only works on some mobile browsers (Android Chrome
// once fullscreen), and only from a user gesture. Desktop is skipped. iOS
// Safari typically has no lock — cinema already sizes to visualViewport, and
// a quiet portrait hint asks them to turn the phone.
function shouldLockCinemaOrientation() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  if (!window.matchMedia("(pointer: coarse)").matches) return false;
  if (window.innerWidth >= 1024 && window.innerWidth > window.innerHeight) {
    return false;
  }
  return true;
}

function lockCinemaOrientation() {
  if (!shouldLockCinemaOrientation()) return;
  const orientation =
    typeof window !== "undefined" && window.screen
      ? window.screen.orientation
      : null;
  if (!orientation || typeof orientation.lock !== "function") return;
  try {
    const result = orientation.lock("landscape");
    if (result && result.catch) result.catch(() => {});
  } catch (err) {
    /* iOS Safari and many desktops decline */
  }
}

function unlockCinemaOrientation() {
  const orientation =
    typeof window !== "undefined" && window.screen
      ? window.screen.orientation
      : null;
  if (!orientation || typeof orientation.unlock !== "function") return;
  try {
    orientation.unlock();
  } catch (err) {
    /* already unlocked or unsupported */
  }
}

function reelCoverSize(box) {
  const target = 16 / 9;
  const aspect = box.width / Math.max(box.height, 1);
  let width;
  let height;
  if (aspect > target) {
    width = box.width;
    height = box.width / target;
  } else {
    height = box.height;
    width = box.height * target;
  }
  // A slight crop hides YouTube's corner mark without the 26% blow-up
  // used in the wooden frame.
  const crop = 1.04;
  return {
    width: Math.round(width * crop),
    height: Math.round(height * crop),
  };
}

function applyCinemaBox(el, box, zIndex = CINEMA_Z) {
  if (!el || !box) return;
  el.style.position = "fixed";
  el.style.zIndex = zIndex;
  el.style.inset = "auto";
  el.style.left = `${Math.round(box.left)}px`;
  el.style.top = `${Math.round(box.top)}px`;
  el.style.width = `${Math.round(box.width)}px`;
  el.style.height = `${Math.round(box.height)}px`;
  el.style.right = "auto";
  el.style.bottom = "auto";
  el.style.maxWidth = "none";
  el.style.maxHeight = "none";
  el.style.aspectRatio = "auto";
  el.style.margin = "0";
  el.style.transform = "none";
  if (zIndex === CINEMA_Z) {
    const reel = reelCoverSize(box);
    el.style.setProperty("--reel-w", `${reel.width}px`);
    el.style.setProperty("--reel-h", `${reel.height}px`);
  } else {
    el.style.removeProperty("--reel-w");
    el.style.removeProperty("--reel-h");
  }
}

function syncReelPlayer(player, box, cinemaOpen) {
  if (!player || !player.setSize || !box) return;
  if (cinemaOpen) {
    const reel = reelCoverSize(box);
    player.setSize(reel.width, reel.height);
  } else {
    player.setSize(Math.round(box.width), Math.round(box.height));
  }
}

function IconSpeaker({ on }) {
  return (
    <svg className="reel-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.2 8.8h3.2L12 5.2v13.6l-4.6-3.6H4.2V8.8z"
      />
      {on ? (
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          d="M15.2 9.2c1.5 1.3 1.5 4.3 0 5.6M17.8 7c2.6 2.2 2.6 7.8 0 10"
        />
      ) : (
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          d="M15.4 9.1l5.2 5.8M20.6 9.1l-5.2 5.8"
        />
      )}
    </svg>
  );
}

function IconPlayPause({ paused }) {
  return paused ? (
    <svg className="reel-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M8 5.4l11 6.6-11 6.6V5.4z" />
    </svg>
  ) : (
    <svg className="reel-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M7.2 5.2h3.2v13.6H7.2V5.2zm6.4 0h3.2v13.6h-3.2V5.2z" />
    </svg>
  );
}

function ReelTransport({
  soundOn,
  paused,
  disabled,
  onSound,
  onPause,
  stopClicks,
}) {
  const wrap = (fn) => (e) => {
    if (stopClicks) e.stopPropagation();
    fn();
  };
  return (
    <>
      <button
        type="button"
        className={`reel-ctrl${soundOn ? " is-on" : ""}`}
        onClick={wrap(onSound)}
        aria-label={soundOn ? "Mute" : "Unmute"}
        aria-pressed={soundOn}
        disabled={disabled}
      >
        <IconSpeaker on={soundOn} />
      </button>
      <button
        type="button"
        className="reel-ctrl"
        onClick={wrap(onPause)}
        aria-label={paused ? "Play" : "Pause"}
        aria-pressed={paused}
        disabled={disabled}
      >
        <IconPlayPause paused={paused} />
      </button>
    </>
  );
}

function ScreeningRoom() {
  const theaterRef = useRef(null);
  const trackRef = useRef(null);
  const itemRefs = useRef([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [frontStill, setFrontStill] = useState(CINEMA[0].still);
  const [backStill, setBackStill] = useState(CINEMA[0].still);
  const [frontOnTop, setFrontOnTop] = useState(true);
  const [reelIndex, setReelIndex] = useState(null);
  const [reelLive, setReelLive] = useState(false);
  // A preference, not per-title state: once sound is on it stays on, which is
  // what lets a new title announce itself before its picture appears.
  const [soundOn, setSoundOn] = useState(false);
  const [reelPaused, setReelPaused] = useState(false);
  const reelHostRef = useRef(null);
  const playerRef = useRef(null);
  // When playback was last interrupted; the reel only reappears once it has
  // been playing cleanly for REEL_REVEAL_MS after this.
  const disruptedAtRef = useRef(0);
  const selectedIndexRef = useRef(0);
  const displayedStillRef = useRef(CINEMA[0].still);
  const frontOnTopRef = useRef(true);
  const didCenterRef = useRef(false);
  const wellRef = useRef(null);
  const screenRef = useRef(null);
  const cinemaFromRef = useRef(null);
  const cinemaChromeTimer = useRef(null);
  const [cinema, setCinema] = useState(false);
  const [cinemaChrome, setCinemaChrome] = useState(false);
  const [nativeFs, setNativeFs] = useState(false);
  const [fsHint, setFsHint] = useState(false);
  const cinemaRef = useRef(false);
  cinemaRef.current = cinema;

  const cinemaDraw = useCinemaDraw();
  const drawOpen = CINEMA_DRAW_ENABLED && cinemaDraw.open;

  const selected = CINEMA[selectedIndex];

  // Only the settled title gets a player, and only while it stays selected.
  const reelId =
    reelIndex === selectedIndex ? youTubeId(CINEMA[reelIndex].video) : "";

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/screening-room",
      title: "Favorite Cinema",
    });
  }, []);

  // Home is a long page; SPA navigation reuses the document scroll. Pin to
  // the title through layout, paint, and the late YouTube focus-scroll —
  // then stop once the visitor actually scrolls. Discrete timers are not
  // enough: playVideo() can focus the iframe seconds later.
  const didOpenRef = useRef(false);
  useLayoutEffect(() => {
    resetPageScroll();
    return holdPageAtTop(8000);
  }, []);

  useLayoutEffect(() => {
    const onFocusIn = (e) => {
      const t = e.target;
      if (!t || t.tagName !== "IFRAME") return;
      if (isPinningPageTop()) resetPageScroll();
    };
    document.addEventListener("focusin", onFocusIn, true);
    return () => document.removeEventListener("focusin", onFocusIn, true);
  }, []);

  const updateSelection = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const mid = track.scrollLeft + track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    itemRefs.current.forEach((el, i) => {
      if (!el) return;
      const center = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(center - mid);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    if (best !== selectedIndexRef.current) {
      selectedIndexRef.current = best;
      setSelectedIndex(best);
    }
  }, []);

  const scrollToIndex = useCallback((index, behavior) => {
    const track = trackRef.current;
    const el = itemRefs.current[index];
    if (!track || !el) return;
    const left = el.offsetLeft - track.clientWidth / 2 + el.offsetWidth / 2;
    track.scrollTo({
      left,
      behavior: behavior || (prefersReducedMotion() ? "auto" : "smooth"),
    });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateSelection();
        ticking = false;
      });
    };

    const onWheel = (e) => {
      if (cinemaRef.current) return;
      if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;
      if (e.deltaY === 0) return;
      e.preventDefault();
      track.scrollLeft += e.deltaY;
    };

    const theater = theaterRef.current;
    track.addEventListener("scroll", onScroll, { passive: true });
    if (theater) theater.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("resize", updateSelection);

    if (!didCenterRef.current) {
      didCenterRef.current = true;
      scrollToIndex(0, "auto");
      updateSelection();
    }

    return () => {
      track.removeEventListener("scroll", onScroll);
      if (theater) theater.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", updateSelection);
    };
  }, [scrollToIndex, updateSelection]);

  useEffect(() => {
    const next = CINEMA[selectedIndex].still;
    if (next === displayedStillRef.current) return;
    displayedStillRef.current = next;
    if (frontOnTopRef.current) {
      setBackStill(next);
      frontOnTopRef.current = false;
      setFrontOnTop(false);
    } else {
      setFrontStill(next);
      frontOnTopRef.current = true;
      setFrontOnTop(true);
    }
  }, [selectedIndex]);

  useEffect(() => {
    [selectedIndex - 1, selectedIndex + 1].forEach((i) => {
      if (!CINEMA[i]) return;
      const img = new Image();
      img.src = CINEMA[i].still;
    });
  }, [selectedIndex]);

  useEffect(() => {
    setReelLive(false);
    setReelPaused(false);
    if (prefersReducedMotion() || !youTubeId(CINEMA[selectedIndex].video)) {
      setReelIndex(null);
      return undefined;
    }
    const settle = setTimeout(() => setReelIndex(selectedIndex), REEL_SETTLE_MS);
    return () => clearTimeout(settle);
  }, [selectedIndex]);

  useEffect(() => {
    if (reelIndex === null) return undefined;
    const item = CINEMA[reelIndex];
    const videoId = youTubeId(item.video);
    if (!videoId) return undefined;

    let cancelled = false;
    const host = reelHostRef.current;
    // The API replaces whatever element it is handed with an iframe, so it gets
    // a throwaway child rather than the host React owns — otherwise destroy()
    // pulls a node out from under React and unmounting throws.
    loadYouTubeApi().then((YT) => {
      if (cancelled || !YT || !host) return;
      const mount = document.createElement("span");
      host.appendChild(mount);
      const x = window.scrollX;
      const y = window.scrollY;
      const keepScroll = () => {
        hushReelFrame(playerRef.current);
        if (isPinningPageTop()) resetPageScroll();
        else window.scrollTo(x, y);
      };
      const player = new YT.Player(mount, {
        videoId,
        host: "https://www.youtube.com",
        playerVars: reelPlayerVars(reelTimings(item).from),
        events: {
          onReady: (event) => {
            event.target.mute();
            silenceCaptions(event.target);
            disruptedAtRef.current = Date.now();
            hushReelFrame(event.target, item.title);
            playReel(event.target);
            window.setTimeout(() => {
              if (cancelled) return;
              try {
                if (event.target.getPlayerState() !== 1) playReel(event.target);
              } catch (err) {
                /* player torn down */
              }
            }, 400);
          },
          onApiChange: (event) => silenceCaptions(event.target),
          onError: () => {
            disruptedAtRef.current = Date.now();
            setReelLive(false);
          },
          onStateChange: (event) => {
            silenceCaptions(event.target);
            if (event.data === YT.PlayerState.ENDED) {
              disruptedAtRef.current = Date.now();
              setReelLive(false);
              event.target.seekTo(reelTimings(item).from);
              playReel(event.target);
              return;
            }
            if (event.data === YT.PlayerState.PLAYING) {
              setReelPaused(false);
              return;
            }
            if (event.data === YT.PlayerState.BUFFERING) {
              // Mid-stream buffer does not bring chrome back. Resetting the
              // still-hold here is why a playing reel never appeared live.
              return;
            }
            disruptedAtRef.current = Date.now();
            setReelLive(false);
            setReelPaused(event.data === YT.PlayerState.PAUSED);
          },
        },
      });
      playerRef.current = player;
      hushReelFrame(player, item.title);
      keepScroll();
      requestAnimationFrame(keepScroll);
    });

    return () => {
      cancelled = true;
      const player = playerRef.current;
      playerRef.current = null;
      if (player && player.destroy) player.destroy();
      if (host) host.innerHTML = "";
    };
  }, [reelIndex]);

  // Browsers only grant audio off a user gesture, so this stays a button. The
  // click also unmutes straight away when sound is already due, rather than
  // waiting up to a tick for the timer below to notice.
  const toggleReelSound = useCallback(() => {
    setSoundOn((wasOn) => {
      const player = playerRef.current;
      const due = Date.now() - disruptedAtRef.current >= REEL_AUDIO_AT_MS;
      if (player) {
        if (!wasOn && due) {
          player.unMute();
          player.setVolume(100);
        } else if (wasOn) {
          player.mute();
        }
      }
      return !wasOn;
    });
  }, []);

  const toggleReelPause = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    if (reelPaused) playReel(player);
    else player.pauseVideo();
  }, [reelPaused]);

  // Egg overlay: hush the reel in place. Do not remount the player.
  useEffect(() => {
    if (!drawOpen) return undefined;
    const player = playerRef.current;
    if (player) {
      try {
        if (player.pauseVideo) player.pauseVideo();
        if (player.mute) player.mute();
      } catch (err) {
        /* player may not be ready */
      }
    }
    return () => {
      const p = playerRef.current;
      if (!p || !p.playVideo) return;
      try {
        playReel(p);
      } catch (err) {
        /* destroyed on title change */
      }
    };
  }, [drawOpen]);

  const pokeCinemaChrome = useCallback(() => {
    setCinemaChrome(true);
    if (cinemaChromeTimer.current) clearTimeout(cinemaChromeTimer.current);
    cinemaChromeTimer.current = setTimeout(() => setCinemaChrome(false), 2200);
  }, []);

  const stepCinema = useCallback(
    (delta) => {
      if (!cinemaRef.current || cinemaRef.current === "out") return;
      const next =
        (selectedIndexRef.current + delta + CINEMA.length) % CINEMA.length;
      selectedIndexRef.current = next;
      setSelectedIndex(next);
      scrollToIndex(next);
      pokeCinemaChrome();
    },
    [scrollToIndex, pokeCinemaChrome]
  );

  const enterCinema = useCallback(() => {
    if (cinema) return;
    if (!youTubeId(CINEMA[selectedIndexRef.current].video)) return;
    const well = wellRef.current;
    const el = screenRef.current;
    if (!well || !el) return;
    cinemaFromRef.current = well.getBoundingClientRect();
    setCinema("in");
    pokeCinemaChrome();
    // Same tap — do not defer. A timeout here expires the gesture and
    // leaves only the CSS cover (browser tabs still visible).
    requestCinemaFullscreen(el, playerRef.current).then((ok) => {
      if (ok) setNativeFs(true);
    });
    lockCinemaOrientation();
  }, [cinema, pokeCinemaChrome]);

  const exitCinema = useCallback(() => {
    if (!cinema || cinema === "out") return;
    setCinemaChrome(false);
    setNativeFs(false);
    unlockCinemaOrientation();
    const el = screenRef.current;
    if (el) {
      el.style.transition = "none";
      applyCinemaBox(el, viewportBox());
    }
    if (nativeFsElement()) {
      exitNativeFs();
      return;
    }
    setCinema("out");
  }, [cinema]);

  useLayoutEffect(() => {
    const el = screenRef.current;
    const well = wellRef.current;
    if (!el) return undefined;

    const pinToWell = () => {
      if (!well) return;
      const box = well.getBoundingClientRect();
      applyCinemaBox(el, box, WELL_Z);
      syncReelPlayer(playerRef.current, box, false);
    };

    if (!cinema) {
      el.style.transition = "none";
      if (!didOpenRef.current) {
        resetPageScroll();
        didOpenRef.current = true;
      }
      pinToWell();
      window.addEventListener("scroll", pinToWell, true);
      window.addEventListener("resize", pinToWell);
      const vv = window.visualViewport;
      if (vv) {
        vv.addEventListener("resize", pinToWell);
        vv.addEventListener("scroll", pinToWell);
      }
      return () => {
        window.removeEventListener("scroll", pinToWell, true);
        window.removeEventListener("resize", pinToWell);
        if (vv) {
          vv.removeEventListener("resize", pinToWell);
          vv.removeEventListener("scroll", pinToWell);
        }
      };
    }

    const reduced = prefersReducedMotion();
    const ms = CINEMA_EXPAND_MS;

    if (reduced) {
      if (cinema === "in") {
        fitCinemaStage(el, playerRef.current);
        setCinema(true);
      } else if (cinema === "out") {
        pinToWell();
        setCinema(false);
      }
      return undefined;
    }

    if (cinema === "in") {
      if (nativeFsElement()) {
        fitCinemaStage(el, playerRef.current);
        setCinema(true);
        return undefined;
      }
      const first = well ? well.getBoundingClientRect() : cinemaFromRef.current;
      if (first) applyCinemaBox(el, first);
      el.style.transition = "none";
      let cancelled = false;
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) return;
          el.style.transition = `left ${ms}ms ${CINEMA_EASE}, top ${ms}ms ${CINEMA_EASE}, width ${ms}ms ${CINEMA_EASE}, height ${ms}ms ${CINEMA_EASE}`;
          fitCinemaStage(el, playerRef.current);
        });
      });
      const done = (e) => {
        if (e && e.propertyName && e.propertyName !== "width") return;
        setCinema(true);
      };
      el.addEventListener("transitionend", done);
      const fallback = setTimeout(done, ms + 80);
      return () => {
        cancelled = true;
        cancelAnimationFrame(id);
        el.removeEventListener("transitionend", done);
        clearTimeout(fallback);
      };
    }

    if (cinema === "out") {
      const back = well ? well.getBoundingClientRect() : cinemaFromRef.current;
      el.style.transition = `left ${ms}ms ${CINEMA_EASE}, top ${ms}ms ${CINEMA_EASE}, width ${ms}ms ${CINEMA_EASE}, height ${ms}ms ${CINEMA_EASE}`;
      if (back) {
        applyCinemaBox(el, back, WELL_Z);
        syncReelPlayer(playerRef.current, back, false);
      }
      const done = (e) => {
        if (e && e.propertyName && e.propertyName !== "width") return;
        setCinema(false);
      };
      el.addEventListener("transitionend", done);
      const fallback = setTimeout(done, ms + 80);
      return () => {
        el.removeEventListener("transitionend", done);
        clearTimeout(fallback);
      };
    }

    fitCinemaStage(el, playerRef.current);
    const keepFull = () => {
      if (cinemaRef.current === "out") return;
      el.style.transition = "none";
      fitCinemaStage(el, playerRef.current);
    };
    let orientTimer = 0;
    const keepFullSoon = () => {
      keepFull();
      window.clearTimeout(orientTimer);
      orientTimer = window.setTimeout(keepFull, 160);
    };
    window.addEventListener("resize", keepFull);
    window.addEventListener("orientationchange", keepFullSoon);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", keepFull);
      vv.addEventListener("scroll", keepFull);
    }
    const orientation =
      typeof window !== "undefined" && window.screen
        ? window.screen.orientation
        : null;
    if (orientation && orientation.addEventListener) {
      orientation.addEventListener("change", keepFullSoon);
    }
    return () => {
      window.clearTimeout(orientTimer);
      window.removeEventListener("resize", keepFull);
      window.removeEventListener("orientationchange", keepFullSoon);
      if (vv) {
        vv.removeEventListener("resize", keepFull);
        vv.removeEventListener("scroll", keepFull);
      }
      if (orientation && orientation.removeEventListener) {
        orientation.removeEventListener("change", keepFullSoon);
      }
    };
  }, [cinema]);

  useEffect(() => {
    document.documentElement.classList.toggle("is-cinema-open", !!cinema);
    return () => document.documentElement.classList.remove("is-cinema-open");
  }, [cinema]);

  useEffect(() => {
    if (!cinema) setNativeFs(!!nativeFsElement());
  }, [cinema]);

  useEffect(() => {
    if (!cinema || cinema === "out" || nativeFs) {
      setFsHint(false);
      return undefined;
    }
    const t = setTimeout(() => {
      if (!nativeFsElement()) setFsHint(true);
    }, 800);
    return () => clearTimeout(t);
  }, [cinema, nativeFs]);

  useEffect(() => {
    const onFs = () => {
      const fs = nativeFsElement();
      setNativeFs(!!fs);
      if (fs) {
        lockCinemaOrientation();
        const el = screenRef.current;
        if (el && cinemaRef.current && cinemaRef.current !== "out") {
          el.style.transition = "none";
          fitCinemaStage(el, playerRef.current);
        }
        return;
      }
      unlockCinemaOrientation();
      if (!cinemaRef.current || cinemaRef.current === "out") return;
      setCinema("out");
    };
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  useEffect(() => {
    if (!cinema) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        stepCinema(-1);
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        stepCinema(1);
        return;
      }
      if (e.key !== "Escape") return;
      if (nativeFsElement()) return;
      e.preventDefault();
      exitCinema();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cinema, exitCinema, stepCinema]);

  useEffect(() => {
    if (cinema) return undefined;
    unlockCinemaOrientation();
    return undefined;
  }, [cinema]);

  useEffect(() => {
    return () => {
      if (cinemaChromeTimer.current) clearTimeout(cinemaChromeTimer.current);
      unlockCinemaOrientation();
    };
  }, []);

  // Runs the cue: silence over the still, then sound, then the picture. The
  // reveal also waits out the player's own controls (see REEL_REVEAL_MS).
  useEffect(() => {
    if (reelIndex === null) return undefined;
    const { revealAt } = reelTimings(CINEMA[reelIndex]);
    const tick = setInterval(() => {
      const player = playerRef.current;
      if (!player || !player.getPlayerState) return;
      const playing = player.getPlayerState() === 1;
      const at = player.getCurrentTime ? player.getCurrentTime() : 0;
      const since = Date.now() - disruptedAtRef.current;

      const wantSound = soundOn && !drawOpen && since >= REEL_AUDIO_AT_MS;
      if (player.isMuted) {
        if (wantSound && player.isMuted()) {
          player.unMute();
          player.setVolume(100);
        } else if (!wantSound && !player.isMuted()) {
          player.mute();
        }
      }

      const needsCue = (CINEMA[reelIndex].videoStart || 0) > 0;
      setReelLive(
        playing && since >= REEL_REVEAL_MS && (!needsCue || at >= revealAt)
      );
    }, 250);
    return () => clearInterval(tick);
  }, [reelIndex, soundOn, drawOpen]);

  return (
    <div className={`TheScreeningRoom${cinema ? " is-cinema-open" : ""}`}>
      <Helmet bodyAttributes={{ style: "background-color: #12100e" }}>
        <title>Favorite Cinema</title>
      </Helmet>
      <main>
        <section>
          <Link to="/" className="back-link">
            <span className="back-caret">&#x3008;</span> Home
          </Link>

          <h1 className="cinema-title" {...cinemaDraw.titleProps}>
            Favorite Cinema<span className="cinema-title-dot">.</span>
          </h1>
          <p className="room-subtitle">
            Scenes, trailers &amp; inevitable spoilers.
          </p>

          <div
            className="theater"
            ref={theaterRef}
            aria-label="Private screening room"
          >
            <div
              className="theater-frame"
              style={{ "--cinema-glow": selected.glow || "190, 170, 130" }}
            >
              <span className="screen-bloom" aria-hidden="true" />
              <div className="proscenium">
                <div className="screen-bezel">
                  <div className="screen-well" ref={wellRef} />
                  {createPortal(
                    <div
                      className={`screen is-lifted${
                        cinema ? " is-cinema" : ""
                      }${cinema && cinemaChrome ? " is-chrome" : ""}${
                        cinema && fsHint ? " is-css-cover" : ""
                      }`}
                      ref={screenRef}
                      role={
                        cinema
                          ? "dialog"
                          : youTubeId(selected.video)
                          ? "button"
                          : undefined
                      }
                      aria-modal={cinema ? "true" : undefined}
                      aria-label={
                        cinema
                          ? selected.title
                          : youTubeId(selected.video)
                          ? "Enter Cinema"
                          : undefined
                      }
                      tabIndex={youTubeId(selected.video) ? 0 : -1}
                      onFocus={() => {
                        if (isPinningPageTop()) resetPageScroll();
                      }}
                      onClick={() => {
                        if (cinema) pokeCinemaChrome();
                        else enterCinema();
                      }}
                      onMouseMove={() => {
                        if (cinema) pokeCinemaChrome();
                      }}
                      onKeyDown={(e) => {
                        if (cinema) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          enterCinema();
                        }
                      }}
                    >
                    <img
                      className={`screen-still${frontOnTop ? " is-active" : ""}`}
                      src={frontStill}
                      alt=""
                    />
                    <img
                      className={`screen-still${!frontOnTop ? " is-active" : ""}`}
                      src={backStill}
                      alt=""
                    />
                    {reelId && (
                      <span
                        className={`screen-reel${reelLive ? " is-live" : ""}`}
                      >
                        <span
                          key={reelId}
                          ref={reelHostRef}
                          className="screen-reel-host"
                        />
                      </span>
                    )}
                    <span className="screen-vignette" />
                    <span className="screen-grain" />
                    {youTubeId(selected.video) && !cinema && (
                      <span className="enter-cinema" aria-hidden="true">
                        Enter Cinema ↗
                      </span>
                    )}
                    {cinema && (
                      <span className="cinema-rotate-hint" aria-hidden="true">
                        Turn your phone sideways
                      </span>
                    )}
                    {cinema && fsHint && (
                      <span className="cinema-fs-hint" aria-hidden="true">
                        This browser keeps its toolbar
                      </span>
                    )}
                    {cinema && (
                      <span className="cinema-hud">
                        <button
                          type="button"
                          className="cinema-close"
                          onClick={(e) => {
                            e.stopPropagation();
                            exitCinema();
                          }}
                          aria-label="Exit Cinema"
                        >
                          ×
                        </button>
                        <button
                          type="button"
                          className="cinema-skip is-prev"
                          onClick={(e) => {
                            e.stopPropagation();
                            stepCinema(-1);
                          }}
                          aria-label="Previous title"
                        >
                          ‹
                        </button>
                        <button
                          type="button"
                          className="cinema-skip is-next"
                          onClick={(e) => {
                            e.stopPropagation();
                            stepCinema(1);
                          }}
                          aria-label="Next title"
                        >
                          ›
                        </button>
                        <span className="cinema-hud-controls">
                          <ReelTransport
                            soundOn={soundOn}
                            paused={reelPaused}
                            onSound={toggleReelSound}
                            onPause={toggleReelPause}
                            stopClicks
                          />
                        </span>
                      </span>
                    )}
                    </div>,
                    document.body
                  )}
                </div>
              </div>

              <p className="screen-caption">
                <span className="screen-title">{selected.title}</span>
                <span className="screen-credit">
                  {selected.year}
                  {selected.by ? ` · ${selected.by}` : ""}
                </span>
                <span
                  className={`reel-controls${
                    youTubeId(selected.video) ? "" : " is-idle"
                  }`}
                >
                  <ReelTransport
                    soundOn={soundOn}
                    paused={reelPaused}
                    disabled={!youTubeId(selected.video)}
                    onSound={toggleReelSound}
                    onPause={toggleReelPause}
                  />
                </span>
              </p>

              <div className="poster-shelf">
                <div
                  className="shelf-track"
                  ref={trackRef}
                  role="listbox"
                  aria-label="Framed posters"
                  aria-activedescendant={`poster-${selectedIndex}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowRight") {
                      e.preventDefault();
                      scrollToIndex(
                        Math.min(CINEMA.length - 1, selectedIndex + 1)
                      );
                    } else if (e.key === "ArrowLeft") {
                      e.preventDefault();
                      scrollToIndex(Math.max(0, selectedIndex - 1));
                    }
                  }}
                >
                  {CINEMA.map((item, i) => {
                    const isSelected = i === selectedIndex;
                    return (
                      <button
                        key={item.title}
                        id={`poster-${i}`}
                        ref={(el) => {
                          itemRefs.current[i] = el;
                        }}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`poster-frame${
                          isSelected ? " is-selected" : ""
                        }`}
                        onClick={() => scrollToIndex(i)}
                        aria-label={`${item.title}, ${item.year}`}
                      >
                        <span className="poster-mat">
                          <img
                            src={item.poster}
                            alt=""
                            draggable="false"
                            decoding="async"
                            loading={i < 6 ? "eager" : "lazy"}
                          />
                        </span>
                        <span className="poster-glass" aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
                <div className="shelf-board" aria-hidden="true" />
              </div>
            </div>
          </div>
        </section>
      </main>
      {drawOpen && (
        <CinemaDraw collection={CINEMA} onClose={cinemaDraw.close} />
      )}
    </div>
  );
}

export default ScreeningRoom;
